
import { useState, useEffect } from "react";
import { Group, Product } from "@/lib/types";
import GroupCard from "@/components/GroupCard";
import GroupUsageForm from "@/components/group/GroupUsageForm";
import { sortByUsageAndAlphabet, getGroupUsageCount } from "@/lib/services/usageFrequencyService";
import { ProductQuantity } from "@/components/group/GroupProductQuantities";
import { supabase } from "@/integrations/supabase/client";

interface GroupsListProps {
  groups: Group[];
  products: Product[];
  onEdit: (group: Group) => void;
  onDelete: (groupId: string) => void;
  onRecordUsage: (groupId: string, quantities?: Record<string, number>, date?: Date) => void;
  groupForUsage?: string | null;
  clearGroupForUsage?: () => void;
}

const GroupsList = ({ 
  groups,
  products,
  onEdit, 
  onDelete, 
  onRecordUsage,
  groupForUsage,
  clearGroupForUsage
}: GroupsListProps) => {
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [showUsageForm, setShowUsageForm] = useState(false);
  const [groupProductQuantities, setGroupProductQuantities] = useState<Record<string, ProductQuantity[]>>({});
  
  // Load saved product quantities for all groups
  useEffect(() => {
    const loadGroupQuantities = async () => {
      if (groups.length === 0) return;
      
      try {
        const { data, error } = await supabase
          .from('group_product_quantities')
          .select('group_id, product_id, quantity');
          
        if (error) throw error;
        
        if (data && data.length > 0) {
          const quantitiesByGroup: Record<string, ProductQuantity[]> = {};
          
          data.forEach(item => {
            if (!quantitiesByGroup[item.group_id]) {
              quantitiesByGroup[item.group_id] = [];
            }
            
            quantitiesByGroup[item.group_id].push({
              productId: item.product_id,
              quantity: item.quantity
            });
          });
          
          setGroupProductQuantities(quantitiesByGroup);
        }
      } catch (err) {
        console.error("Error loading group product quantities:", err);
      }
    };
    
    loadGroupQuantities();
  }, [groups]);

  const handleRecordUsage = (group: Group) => {
    setSelectedGroup(group);
    setShowUsageForm(true);
  };
  
  // Handle auto-opening of usage form when navigating directly to /groups/:groupId/usage
  useEffect(() => {
    if (groupForUsage && clearGroupForUsage) {
      const group = groups.find(g => g.id === groupForUsage);
      if (group) {
        setSelectedGroup(group);
        setShowUsageForm(true);
        clearGroupForUsage();
      }
    }
  }, [groupForUsage, groups, clearGroupForUsage]);
  
  // Handle form close
  const handleFormOpenChange = (open: boolean) => {
    setShowUsageForm(open);
    if (!open && clearGroupForUsage) {
      clearGroupForUsage();
    }
  };

  // Sort groups by usage frequency, then alphabetically
  const sortedGroups = sortByUsageAndAlphabet(groups, 'group', (group) => 
    getGroupUsageCount(group.id, products.filter(p => p.groupIds.includes(group.id)))
  );
  
  return (
    <>
      <div className="card-grid">
        {sortedGroups.map((group) => (
          <GroupCard
            key={group.id}
            group={group}
            products={products}
            onEdit={onEdit}
            onDelete={() => onDelete(group.id)}
            onRecordUsage={() => handleRecordUsage(group)}
          />
        ))}
      </div>
      
      {selectedGroup && (
        <GroupUsageForm
          open={showUsageForm}
          onOpenChange={handleFormOpenChange}
          group={selectedGroup}
          products={products}
          onRecordUsage={onRecordUsage}
          defaultQuantity={1}
          savedQuantities={
            groupProductQuantities[selectedGroup.id]?.reduce((acc, item) => {
              acc[item.productId] = item.quantity;
              return acc;
            }, {} as Record<string, number>) || {}
          }
        />
      )}
    </>
  );
};

export default GroupsList;
